/**
 * As folhas em escala 1:1, fora da tela. Serve a três coisas:
 *  1. impressão: no @media print só esta área aparece, uma folha por página;
 *  2. PDF/imagem: a captura lê daqui (exportacao.js);
 *  3. ajuste medido: quando as linhas com dado não cabem num papel, a folha
 *     sobe um degrau (linhas mais baixas, letra menor...) via `onAjustar`.
 *
 * Fica num portal no <body>, fora do #root, para o CSS de impressão poder
 * esconder o app inteiro com uma regra só.
 */
import React, { useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { createGlobalStyle } from "styled-components";

import { proximoAjuste } from "../../utils/apontamentos";
import Folha from "./folha/Folha";
import { escaparSeletor } from "./seletor";

const EstiloDeImpressao = createGlobalStyle`
  [data-area-impressao] {
    position: absolute;
    top: 0;
    left: -20000px;
    width: 1123px;
    pointer-events: none;
  }

  @page {
    size: A4 landscape;
    margin: 0;
  }

  @page retrato {
    size: A4 portrait;
    margin: 0;
  }

  @media print {
    html,
    body {
      margin: 0 !important;
      padding: 0 !important;
      background: #fff !important;
      height: auto !important;
      overflow: visible !important;
    }

    body > *:not([data-area-impressao]) {
      display: none !important;
    }

    [data-area-impressao] {
      position: static;
      left: auto;
      width: auto;
    }

    [data-area-impressao] [data-folha] {
      width: 297mm;
      height: 210mm;
      break-after: page;
      page-break-after: always;
    }

    [data-area-impressao] [data-folha][data-retrato] {
      page: retrato;
      width: 210mm;
      height: 297mm;
    }

    [data-area-impressao] [data-folha]:last-child {
      break-after: auto;
      page-break-after: auto;
    }
  }
`;

/**
 * Lê do DOM se as linhas com dado de cada folha cabem no corpo da tabela;
 * a que não coube sobe um degrau do ajuste (utils/apontamentos.proximoAjuste).
 * A altura do conteúdo vai do topo da 1ª linha ao pé da última (já com a
 * escala aplicada, porque getBoundingClientRect enxerga o transform).
 */
function medir(raiz, folhas, ajusteDe) {
  const mudancas = {};
  folhas.filter((folha) => folha.tipo === "setor" && folha.linhas.length > 0).forEach((folha) => {
    const elemento = raiz.querySelector(`[data-folha="${escaparSeletor(folha.chave)}"]`);
    const corpo = elemento?.querySelector("[data-corpo]");
    const linhas = elemento ? [...elemento.querySelectorAll("[data-dados]")] : [];
    if (!corpo || linhas.length !== folha.linhas.length) return;
    const alturaDoConteudo = linhas[linhas.length - 1].getBoundingClientRect().bottom - linhas[0].getBoundingClientRect().top;
    const atual = ajusteDe(folha);
    const proximo = proximoAjuste(atual, { alturaDoConteudo, alturaUtil: corpo.clientHeight });
    if (proximo !== atual) mudancas[folha.chave] = proximo;
  });
  return mudancas;
}

export default function AreaImpressao({ folhas, logo, vias, ajusteDe, onAjustar }) {
  const raiz = useRef(null);

  useLayoutEffect(() => {
    if (!raiz.current) return;
    const mudancas = medir(raiz.current, folhas, ajusteDe);
    if (Object.keys(mudancas).length) onAjustar(mudancas);
  }, [folhas, ajusteDe, onAjustar]);

  // A fonte da folha muda a altura das linhas: mede de novo quando ela chega.
  useEffect(() => {
    let ativo = true;
    document.fonts?.ready.then(() => {
      if (!ativo || !raiz.current) return;
      const mudancas = medir(raiz.current, folhas, ajusteDe);
      if (Object.keys(mudancas).length) onAjustar(mudancas);
    });
    return () => {
      ativo = false;
    };
  }, [folhas, ajusteDe, onAjustar]);

  return createPortal(
    <div data-area-impressao="" ref={raiz} aria-hidden="true">
      <EstiloDeImpressao />
      {folhas.map((folha) => (
        <Folha
          key={folha.chave}
          folha={folha}
          logo={logo}
          via={vias[`${folha.lote.chave}|${folha.setor.id}`]}
          ajuste={ajusteDe(folha)}
        />
      ))}
    </div>,
    document.body,
  );
}
